import { useEffect, useMemo, useRef, useState } from 'react';
import Combobox from '../base/Combobox';
import { useSaudiCities, type ResolvedSaudiCity } from '../../hooks/useSaudiCities';
import { matchSaudiCity, orderSaudiCities, searchSaudiCities } from '../../config/saudiCities';
import { useNsText } from './searchUi';

/**
 * The Saudi city picker: a searchable list that opens BELOW the field, featured
 * cities first in the owner's order (مكة المكرمة، المدينة المنورة، الرياض، جدة،
 * الدمام), then every other city alphabetically. See Apps/web/THEME.md.
 *
 * - Names come from the canonical list (config/saudiCities), so it works at once and
 *   offline; each city carries its database `cityId` once `GET /locations/cities`
 *   answers (hooks/useSaudiCities). A city picked before that is upgraded to the one
 *   with its `cityId` through `onChange` as soon as the ids arrive.
 * - Search is Arabic-normalized over Arabic names, English names and aliases:
 *   «جده» finds جدة, «مكه» finds مكة المكرمة, "Medina" finds المدينة المنورة.
 * - No listing counts: the endpoint's `propertyCount` includes every status.
 * - Typing then leaving the field keeps an exact match («جده» → جدة); anything else
 *   goes back to the chosen city. Emptying the field means "no city" (`null`).
 */

const ALL_KEY = '__all__';

type CityOption = { kind: 'all' } | { kind: 'city'; city: ResolvedSaudiCity };

export interface CityComboboxProps {
  /** The chosen city, or null for none («كل المدن» when `allowAll`). */
  value: ResolvedSaudiCity | null;
  onChange: (city: ResolvedSaudiCity | null) => void;
  /** Offer «كل المدن» first (picking it sends `null`). Default true. */
  allowAll?: boolean;
  /** Accessible name. Default «المدينة». */
  label?: string;
  /** Default: hidden for 'bar', visible for 'field'. */
  hideLabel?: boolean;
  /** Default «كل المدن» with `allowAll`, else «اختر المدينة». */
  placeholder?: string;
  /** 'bar': borderless cell of the hero search bar. 'field': bordered form field. Default 'field'. */
  variant?: 'bar' | 'field';
  /** 'bar' only: the label small inside the cell, above the value (Combobox `inlineLabel`). */
  inlineLabel?: boolean;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  'aria-describedby'?: string;
  className?: string;
}

function sameCity(a: ResolvedSaudiCity | null, b: ResolvedSaudiCity | null): boolean {
  if (!a || !b) return a === b;
  return a.slug === b.slug && a.cityId === b.cityId;
}

export default function CityCombobox({
  value,
  onChange,
  allowAll = true,
  label,
  hideLabel,
  placeholder,
  variant = 'field',
  inlineLabel = false,
  id,
  disabled = false,
  invalid = false,
  'aria-describedby': ariaDescribedBy,
  className = '',
}: CityComboboxProps) {
  const { text, isArabic } = useNsText('ui');
  const { cities, findBySlug } = useSaudiCities();

  // The hook orders by Arabic name; English readers get the rest A to Z.
  const ordered = useMemo(() => (isArabic ? cities : orderSaudiCities(cities, 'en')), [cities, isArabic]);
  const nameOf = (city: ResolvedSaudiCity) => (isArabic ? city.nameAr : city.nameEn);

  // Text being typed; null while not editing (the field then shows the chosen city).
  const [draft, setDraft] = useState<string | null>(null);
  const query = draft?.trim() ?? '';

  const options = useMemo<CityOption[]>(() => {
    if (query) return searchSaudiCities(query, ordered).map((city) => ({ kind: 'city' as const, city }));
    const all = ordered.map((city) => ({ kind: 'city' as const, city }));
    return allowAll ? [{ kind: 'all' as const }, ...all] : all;
  }, [allowAll, ordered, query]);

  // A city picked before the ids loaded (cityId null): hand back the same city with
  // its id, so the search filters by the exact City.id instead of the fuzzy name.
  // Once per city, so a parent that drops the id cannot make this loop.
  const upgradedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!value || value.cityId) return;
    const resolved = findBySlug(value.slug);
    if (!resolved?.cityId) return;
    const key = `${resolved.slug}:${resolved.cityId}`;
    if (upgradedRef.current === key) return;
    upgradedRef.current = key;
    onChange(resolved);
  }, [value, findBySlug, onChange]);

  const commit = (next: ResolvedSaudiCity | null) => {
    setDraft(null);
    if (!sameCity(next, value)) onChange(next);
  };

  // Leaving the field: an exact name is taken, an empty field means no city,
  // anything else falls back to the chosen city.
  const commitDraft = () => {
    if (draft === null) return;
    const typed = draft.trim();
    if (!typed) {
      commit(null);
      return;
    }
    const match = matchSaudiCity(typed);
    const resolved = match ? findBySlug(match.slug) : undefined;
    if (resolved) commit(resolved);
    else setDraft(null);
  };

  const allLabel = text('city.allCities', 'All cities');
  const resolvedLabel = label ?? text('city.label', 'City');
  const resolvedPlaceholder =
    placeholder ?? (allowAll ? allLabel : text('city.placeholder', 'Choose a city'));
  const selectedKey = value ? value.slug : allowAll ? ALL_KEY : null;

  return (
    <Combobox<CityOption>
      id={id}
      label={resolvedLabel}
      hideLabel={hideLabel ?? variant === 'bar'}
      inputValue={draft ?? (value ? nameOf(value) : '')}
      onInputChange={setDraft}
      options={options}
      getOptionKey={(option) => (option.kind === 'all' ? ALL_KEY : option.city.slug)}
      renderOption={(option) =>
        option.kind === 'all' ? (
          <span className="flex items-center gap-2.5">
            <i className="ri-map-2-line text-[1.125rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true" />
            <span className="min-w-0">{allLabel}</span>
          </span>
        ) : (
          nameOf(option.city)
        )
      }
      onSelect={(option) => commit(option.kind === 'all' ? null : option.city)}
      selectedKey={selectedKey}
      // Only with a city chosen: otherwise «كل المدن» counts as selected and an
      // empty field would still show a clear button.
      onClear={value ? () => commit(null) : undefined}
      placeholder={resolvedPlaceholder}
      leadingIcon={<i className="ri-map-pin-2-line" />}
      variant={variant === 'bar' ? 'bare' : 'field'}
      inlineLabel={inlineLabel && variant === 'bar'}
      openOnFocus
      autoActivateFirst
      disabled={disabled}
      invalid={invalid}
      popoverMinWidth="16rem"
      listLabel={resolvedLabel}
      className={className}
      inputProps={{
        'aria-describedby': ariaDescribedBy,
        onBlur: commitDraft,
      }}
    />
  );
}
